import { useState } from 'react';
import { CpmButton, CpmModal, CpmTypeChip } from '../../../../components/design-system';
import {
    CPM_PENDING_CHAIN_COLLAPSE_AFTER,
    duplicateTargetName,
    packLabel,
} from '../../../../constants/centralProductMaster.constants';
import type { CpmPendingItem } from '../../../../types/centralProductMaster.types';
import type { PendingChainCard } from './buildPendingChains';

const IconSparkle = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="m12 3-1.9 5.8a2 2 0 0 1-1.287 1.288L3 12l5.8 1.9a2 2 0 0 1 1.288 1.287L12 21l1.9-5.8a2 2 0 0 1 1.287-1.288L21 12l-5.8-1.9a2 2 0 0 1-1.288-1.287Z" />
    </svg>
);

const IconBranch = ({ size = 16 }: { size?: number }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="6" y1="3" x2="6" y2="15" />
        <circle cx="18" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
        <path d="M18 9a9 9 0 0 1-9 9" />
    </svg>
);

const IconCheck = ({ size = 13 }: { size?: number }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M20 6 9 17l-5-5" />
    </svg>
);

const IconChevron = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--cpm-text-tertiary)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0"
    >
        <path d="m9 18 6-6-6-6" />
    </svg>
);

const IconConnector = () => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--cpm-text-tertiary)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0"
    >
        <path d="M4 4v10a4 4 0 0 0 4 4h12" />
        <path d="m16 14 4 4-4 4" />
    </svg>
);

const IconLock = () => (
    <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

interface PendingGroupedViewProps {
    groupChains: PendingChainCard[];
    standalone: CpmPendingItem[];
    promotedIds: Set<string>;
    mutating?: boolean;
    onOpen: (item: CpmPendingItem) => void;
    onApproveChain: (nodes: CpmPendingItem[]) => Promise<boolean> | boolean | void;
}

type ChainPhase = 'confirm' | 'working' | 'error' | 'done';

function NodeRow({
    item,
    depth,
    promoted,
    onOpen,
}: {
    item: CpmPendingItem;
    depth: number;
    promoted: boolean;
    onOpen: (item: CpmPendingItem) => void;
}) {
    const blocked = !item.parentIsActive && !!item.parent && !promoted;
    const hasFlag = !!item.possibleDuplicateOf && !blocked && !promoted;
    const pack = packLabel(item);
    const dupName = hasFlag ? duplicateTargetName(item) : '';

    return (
        <div
            onClick={() => onOpen(item)}
            className="flex items-center cursor-pointer transition-colors hover:bg-[var(--cpm-surface-subtle)] border-t border-[var(--cpm-border-default)]"
            style={{
                gap: 12,
                padding: `12px 16px 12px ${16 + depth * 26}px`,
                background: promoted ? 'var(--cpm-success-bg)' : '#fff',
            }}
        >
            {depth > 0 ? <IconConnector /> : null}
            <CpmTypeChip recordType={item.recordType} />
            <div className="flex-1 min-w-0 flex items-center" style={{ gap: 9 }}>
                <span
                    className="min-w-0 truncate text-[var(--cpm-text-primary)]"
                    style={{ fontSize: 13, fontWeight: 700 }}
                >
                    {item.name}
                </span>
                {pack ? (
                    <span
                        className="shrink-0 tabular text-[var(--cpm-text-secondary)] whitespace-nowrap"
                        style={{ fontSize: 11.5, fontWeight: 700 }}
                    >
                        {pack}
                    </span>
                ) : null}
            </div>
            {promoted ? (
                <span
                    className="inline-flex items-center gap-[5px] px-[9px] py-[3px] rounded-full bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)] text-[var(--cpm-success-text-light)] whitespace-nowrap"
                    style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.03em' }}
                >
                    <IconCheck size={11} />
                    Approved
                </span>
            ) : null}
            {blocked ? (
                <span
                    className="inline-flex items-center gap-[5px] px-[9px] py-[3px] rounded-full bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)] whitespace-nowrap"
                    style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em' }}
                >
                    <IconLock />
                    Blocked
                </span>
            ) : null}
            {hasFlag ? (
                <span
                    className="inline-flex items-center px-[9px] py-[3px] rounded-full bg-white border border-[var(--cpm-warning-border)] text-[var(--cpm-warning-text)] max-w-[240px]"
                    style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em' }}
                    title={dupName ? `Possible duplicate of ${dupName}` : 'Possible duplicate'}
                >
                    <span className="whitespace-nowrap">Possible duplicate</span>
                    {dupName ? (
                        <span
                            className="ml-[6px] min-w-0 truncate font-semibold tracking-normal"
                            style={{ fontWeight: 700, letterSpacing: 0 }}
                        >
                            {dupName}
                        </span>
                    ) : null}
                </span>
            ) : null}
            <IconChevron />
        </div>
    );
}

function ChainNodeList({
    chain,
    promotedIds,
    onOpen,
}: {
    chain: PendingChainCard;
    promotedIds: Set<string>;
    onOpen: (item: CpmPendingItem) => void;
}) {
    const [expanded, setExpanded] = useState(false);
    const shouldCollapse = chain.nodes.length > CPM_PENDING_CHAIN_COLLAPSE_AFTER;
    const visibleNodes =
        shouldCollapse && !expanded
            ? chain.nodes.slice(0, CPM_PENDING_CHAIN_COLLAPSE_AFTER)
            : chain.nodes;
    const hiddenCount = chain.nodes.length - visibleNodes.length;

    return (
        <div
            className={shouldCollapse ? 'overflow-y-auto' : undefined}
            style={shouldCollapse ? { maxHeight: 420 } : undefined}
        >
            {visibleNodes.map(({ item, depth }) => (
                <NodeRow
                    key={item._id}
                    item={item}
                    depth={depth}
                    promoted={promotedIds.has(item._id)}
                    onOpen={onOpen}
                />
            ))}
            {shouldCollapse ? (
                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    className="w-full text-left border-t border-[var(--cpm-border-default)] bg-[var(--cpm-surface-subtle)] hover:bg-[var(--cpm-purple-tint-3)] text-[var(--cpm-brand-navy)]"
                    style={{
                        padding: '10px 16px',
                        fontSize: 12.5,
                        fontWeight: 800,
                    }}
                >
                    {expanded
                        ? 'Show fewer records'
                        : `Show all ${chain.nodes.length} records (${hiddenCount} hidden)`}
                </button>
            ) : null}
        </div>
    );
}

export default function PendingGroupedView({
    groupChains,
    standalone,
    promotedIds,
    mutating,
    onOpen,
    onApproveChain,
}: PendingGroupedViewProps) {
    const [confirmChain, setConfirmChain] = useState<PendingChainCard | null>(null);
    const [chainPhase, setChainPhase] = useState<ChainPhase>('confirm');

    const closeChainModal = () => {
        setConfirmChain(null);
        setChainPhase('confirm');
    };

    const runApproveChain = async () => {
        if (!confirmChain) return;
        const nodes = confirmChain.nodes
            .filter((n) => !promotedIds.has(n.item._id))
            .map((n) => n.item);
        setChainPhase('working');
        try {
            const ok = await onApproveChain(nodes);
            if (ok === false) {
                setChainPhase('error');
                return;
            }
            setChainPhase('done');
        } catch {
            setChainPhase('error');
        }
    };

    const approveBtn = (chain: PendingChainCard) => {
        const pendingLeft = chain.nodes.filter((n) => !promotedIds.has(n.item._id));
        const allApproved = pendingLeft.length === 0;
        if (allApproved) {
            return (
                <span
                    className="shrink-0 inline-flex items-center gap-[6px] px-[13px] py-[6px] rounded-full bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)] text-[var(--cpm-success-text-light)] whitespace-nowrap"
                    style={{ fontSize: 11.5, fontWeight: 800 }}
                >
                    <IconCheck size={13} />
                    Chain approved
                </span>
            );
        }
        if (!chain.isGroup) return null;
        return (
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setChainPhase('confirm');
                    setConfirmChain(chain);
                }}
                className="shrink-0 inline-flex items-center text-white bg-[var(--cpm-brand-navy)] shadow-[var(--cpm-shadow-cta)] hover:brightness-110 hover:-translate-y-px transition-all"
                style={{
                    gap: 8,
                    height: 36,
                    paddingLeft: 15,
                    paddingRight: 15,
                    borderRadius: 9,
                    fontSize: 12.5,
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                }}
            >
                <IconSparkle />
                Approve this entire chain
            </button>
        );
    };

    return (
        <>
            <div className="flex flex-col p-[16px]" style={{ gap: 14 }}>
                {groupChains.map((chain) => (
                    <div
                        key={chain.key}
                        className="border border-[var(--cpm-border-default)] rounded-[12px] overflow-hidden shadow-[0_1px_2px_rgba(14,8,88,0.04)]"
                    >
                        {chain.liveAncestor ? (
                            <div
                                className="flex items-center bg-[var(--cpm-surface-subtle)] border-b border-dashed border-[var(--cpm-border-strong)] cursor-pointer transition-colors hover:bg-[var(--cpm-purple-tint-3)]"
                                style={{ gap: 12, padding: '12px 15px' }}
                                onClick={() => {
                                    const first = chain.nodes[0]?.item;
                                    if (first) onOpen(first);
                                }}
                            >
                                <span
                                    className="shrink-0 inline-flex items-center justify-center bg-white border border-[var(--cpm-border-strong)] text-[var(--cpm-text-tertiary)]"
                                    style={{ width: 30, height: 30, borderRadius: 8 }}
                                >
                                    <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path
                                            d="M20 6 9 17l-5-5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                                <div className="flex flex-col min-w-0 flex-1" style={{ gap: 2 }}>
                                    <div
                                        className="flex items-center flex-wrap min-w-0"
                                        style={{ gap: 8 }}
                                    >
                                        <span
                                            className="truncate text-[var(--cpm-text-secondary)]"
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 700,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {chain.liveAncestor.name}
                                        </span>
                                        <CpmTypeChip recordType={chain.liveAncestor.type} />
                                        <span
                                            className="inline-flex items-center gap-[5px] px-[9px] py-[3px] rounded-full bg-white border border-[var(--cpm-border-strong)] text-[var(--cpm-text-tertiary)] uppercase whitespace-nowrap"
                                            style={{
                                                fontSize: 10,
                                                fontWeight: 800,
                                                letterSpacing: '0.05em',
                                            }}
                                        >
                                            Already live
                                        </span>
                                    </div>
                                    <span
                                        className="text-[var(--cpm-text-tertiary)]"
                                        style={{ fontSize: 11.5, fontWeight: 700 }}
                                    >
                                        {chain.chainLabel}
                                    </span>
                                </div>
                                {approveBtn(chain)}
                                <IconChevron />
                            </div>
                        ) : (
                            <div
                                className="flex items-center bg-[var(--cpm-surface-subtle)] border-b border-[var(--cpm-border-default)]"
                                style={{ gap: 12, padding: '12px 15px' }}
                            >
                                <span
                                    className="shrink-0 inline-flex items-center justify-center bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                                    style={{ width: 30, height: 30, borderRadius: 8 }}
                                >
                                    <IconBranch />
                                </span>
                                <div className="flex flex-col min-w-0 flex-1" style={{ gap: 2 }}>
                                    <div className="flex items-center min-w-0" style={{ gap: 8 }}>
                                        <span
                                            className="truncate text-[var(--cpm-brand-navy)]"
                                            style={{
                                                fontSize: 13.5,
                                                fontWeight: 800,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {chain.root.name}
                                        </span>
                                        <CpmTypeChip recordType={chain.root.recordType} />
                                    </div>
                                    <span
                                        className="text-[var(--cpm-text-secondary)]"
                                        style={{ fontSize: 11.5, fontWeight: 700 }}
                                    >
                                        {chain.chainLabel}
                                    </span>
                                </div>
                                {approveBtn(chain)}
                            </div>
                        )}
                        <ChainNodeList chain={chain} promotedIds={promotedIds} onOpen={onOpen} />
                    </div>
                ))}

                {standalone.length > 0 ? (
                    <div className="flex flex-col" style={{ gap: 7 }}>
                        <span
                            className="uppercase text-[var(--cpm-text-tertiary)]"
                            style={{
                                fontSize: 10,
                                fontWeight: 800,
                                letterSpacing: '0.08em',
                                padding: '0 2px',
                            }}
                        >
                            Standalone — no parent or pending relatives to show
                        </span>
                        <div className="border border-[var(--cpm-border-default)] rounded-[12px] overflow-hidden">
                            {standalone.map((item) => (
                                <NodeRow
                                    key={item._id}
                                    item={item}
                                    depth={0}
                                    promoted={promotedIds.has(item._id)}
                                    onOpen={onOpen}
                                />
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>

            <CpmModal open={!!confirmChain} onClose={closeChainModal} maxWidth={540} zIndex={80}>
                {confirmChain && chainPhase === 'confirm' ? (
                    <>
                        <div
                            className="flex items-start border-b border-[var(--cpm-border-default)]"
                            style={{ gap: 13, padding: '20px 22px 16px' }}
                        >
                            <span
                                className="shrink-0 inline-flex items-center justify-center bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                                style={{ width: 38, height: 38, borderRadius: 10 }}
                            >
                                <IconBranch size={19} />
                            </span>
                            <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                                <span
                                    className="inline-flex self-start items-center rounded-full bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)] uppercase"
                                    style={{
                                        padding: '3px 10px',
                                        fontSize: 10,
                                        fontWeight: 800,
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    Approve entire chain
                                </span>
                                <h2 className="m-0 !text-[18px] !font-extrabold !tracking-[-0.02em] !leading-[1.2] !text-[var(--cpm-brand-navy)]">
                                    Approve this entire chain?
                                </h2>
                                <span
                                    className="text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 12, fontWeight: 700 }}
                                >
                                    {confirmChain.liveAncestor?.name ?? confirmChain.root.name} ·{' '}
                                    {confirmChain.chainLabel}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={closeChainModal}
                                className="shrink-0 inline-flex items-center justify-center bg-white border-[1.5px] border-[var(--cpm-border-strong)] text-[var(--cpm-text-secondary)] hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)]"
                                style={{ width: 30, height: 30, borderRadius: 8 }}
                            >
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                >
                                    <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
                        <div
                            className="flex flex-col"
                            style={{ padding: '18px 22px 20px', gap: 13 }}
                        >
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{
                                    fontSize: 10.5,
                                    fontWeight: 800,
                                    letterSpacing: '0.08em',
                                }}
                            >
                                Records that will be promoted
                            </span>
                            <div
                                className="border border-[var(--cpm-border-default)] rounded-[10px] overflow-y-auto"
                                style={{ maxHeight: 280 }}
                            >
                                {confirmChain.nodes
                                    .filter((n) => !promotedIds.has(n.item._id))
                                    .map(({ item }, i) => (
                                        <div
                                            key={item._id}
                                            className="flex items-center"
                                            style={{
                                                gap: 11,
                                                padding: '11px 14px',
                                                background:
                                                    i % 2 ? 'var(--cpm-surface-subtle)' : '#fff',
                                                borderBottom: '1px solid var(--cpm-border-default)',
                                            }}
                                        >
                                            <span
                                                className="shrink-0 inline-flex items-center justify-center rounded-full bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)] text-[var(--cpm-text-tertiary)] tabular"
                                                style={{
                                                    width: 20,
                                                    height: 20,
                                                    fontSize: 10.5,
                                                    fontWeight: 800,
                                                }}
                                            >
                                                {i + 1}
                                            </span>
                                            <CpmTypeChip recordType={item.recordType} />
                                            <span
                                                className="flex-1 min-w-0 truncate text-[var(--cpm-text-primary)]"
                                                style={{ fontSize: 13, fontWeight: 700 }}
                                            >
                                                {item.name}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                            <div
                                className="flex items-start bg-[var(--cpm-surface-subtle)] border border-[var(--cpm-border-default)]"
                                style={{ gap: 10, padding: '11px 13px', borderRadius: 10 }}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="var(--cpm-text-tertiary)"
                                    strokeWidth="2"
                                    className="shrink-0 mt-px"
                                >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
                                </svg>
                                <span
                                    className="text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.5 }}
                                >
                                    Records approve in parent-first order so nothing is left
                                    blocked.
                                </span>
                            </div>
                        </div>
                        <div
                            className="flex items-center justify-end border-t-[1.5px] border-[var(--cpm-border-strong)]"
                            style={{
                                gap: 10,
                                padding: '14px 22px',
                                background: 'rgba(249,249,252,0.88)',
                            }}
                        >
                            <CpmButton variant="outline" size="modal" onClick={closeChainModal}>
                                Cancel
                            </CpmButton>
                            <CpmButton
                                variant="navy"
                                size="modal"
                                leadingIcon={<IconCheck size={15} />}
                                isLoading={mutating}
                                onClick={runApproveChain}
                            >
                                Approve the chain
                            </CpmButton>
                        </div>
                    </>
                ) : null}

                {confirmChain && chainPhase === 'working' ? (
                    <div
                        className="flex flex-col items-center text-center"
                        style={{ padding: '40px 30px 34px', gap: 14 }}
                    >
                        <span
                            className="cpm-spin"
                            style={{ width: 34, height: 34, borderWidth: 3.4 }}
                        />
                        <span
                            className="text-[var(--cpm-brand-navy)]"
                            style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' }}
                        >
                            Promoting{' '}
                            {confirmChain.nodes.filter((n) => !promotedIds.has(n.item._id)).length}{' '}
                            records in this chain
                        </span>
                        <span
                            className="text-[var(--cpm-text-secondary)] max-w-[340px]"
                            style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                        >
                            Records are promoted top-down so nothing is left blocked. Do not close
                            this dialog.
                        </span>
                    </div>
                ) : null}

                {confirmChain && chainPhase === 'error' ? (
                    <>
                        <div
                            className="flex flex-col items-center text-center"
                            style={{ padding: '34px 30px 22px', gap: 12 }}
                        >
                            <span
                                className="inline-flex items-center justify-center bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)] text-[var(--cpm-shortfall-text)]"
                                style={{ width: 52, height: 52, borderRadius: 14 }}
                            >
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                    <path d="M12 9v4" />
                                    <path d="M12 17h.01" />
                                </svg>
                            </span>
                            <span
                                className="text-[var(--cpm-brand-navy)]"
                                style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}
                            >
                                Chain approval failed
                            </span>
                            <span
                                className="text-[var(--cpm-text-secondary)] max-w-[340px]"
                                style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                            >
                                The catalog service rejected the chain part-way through. No records
                                were promoted. Try again.
                            </span>
                        </div>
                        <div
                            className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                            style={{
                                gap: 10,
                                padding: '14px 22px',
                                background: 'rgba(249,249,252,0.88)',
                            }}
                        >
                            <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                                <CpmButton variant="outline" size="modal" onClick={closeChainModal}>
                                    Close
                                </CpmButton>
                                <CpmButton
                                    variant="navy"
                                    size="modal"
                                    onClick={runApproveChain}
                                    leadingIcon={
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                            <path d="M3 3v5h5" />
                                        </svg>
                                    }
                                >
                                    Try again
                                </CpmButton>
                            </div>
                        </div>
                    </>
                ) : null}

                {confirmChain && chainPhase === 'done' ? (
                    <div
                        className="flex flex-col items-center text-center"
                        style={{ padding: '40px 30px 30px', gap: 12 }}
                    >
                        <span
                            className="inline-flex items-center justify-center bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)] text-[var(--cpm-success-text-light)]"
                            style={{ width: 52, height: 52, borderRadius: 14 }}
                        >
                            <IconCheck size={24} />
                        </span>
                        <span
                            className="text-[var(--cpm-brand-navy)]"
                            style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}
                        >
                            Chain promoted
                        </span>
                        <span
                            className="text-[var(--cpm-text-secondary)] max-w-[340px]"
                            style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}
                        >
                            The records in this chain are now Active in the catalog.
                        </span>
                        <CpmButton
                            variant="navy"
                            size="md"
                            className="mt-1.5"
                            onClick={closeChainModal}
                        >
                            Back to the queue
                        </CpmButton>
                    </div>
                ) : null}
            </CpmModal>
        </>
    );
}
